import React, { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, MenuItem } from '@material-ui/core';
import cx from 'clsx';
import Select from 'components/@common/Inputs/Select';
import { Product } from 'types/schema-generated-types';
import {
  getStudentCurriculumDisplayName,
  sortProductCurriculumByGrades
} from 'utilities/product-utils';

interface StudentCurriculumSelectProps {
  productFamilyId: number;
  products: Product[];
  disabledProductIds?: number[];
  groupByProduct?: boolean;
  value?: number | '';
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const StudentCurriculumSelect = ({
  onChange,
  productFamilyId,
  products,
  disabledProductIds = [],
  groupByProduct,
  value = ''
}: StudentCurriculumSelectProps) => {
  const classes = useStyles();
  const { t } = useTranslation('StudentProductLicenseCard');

  const useProductGroups = groupByProduct ?? products.length > 1;

  const assignedProductId = useMemo(() => {
    if (!value) return null;

    return (
      products
        .flatMap(product => product.curriculums ?? [])
        .find(curriculum => curriculum.id === value)?.productId ?? null
    );
  }, [products, value]);

  const menuItems = useMemo(
    () =>
      products.reduce((acc, product) => {
        const curriculums = product.curriculums
          ? [...product.curriculums].sort(sortProductCurriculumByGrades)
          : [];

        if (curriculums.length === 0) return acc;

        if (useProductGroups) {
          acc.push(
            <MenuItem
              key={`family_${productFamilyId}_product_${product.id}`}
              disabled
              className={classes.productMenuItem}
            >
              {product.name}
            </MenuItem>
          );
        }

        const curriculumMenuItems = curriculums.map(curriculum => (
          <MenuItem
            key={`family_${productFamilyId}_curriculum_${curriculum.id}`}
            disabled={disabledProductIds.includes(product.id) && product.id !== assignedProductId}
            value={curriculum.id}
            className={classes.curriculumMenuItem}
          >
            {getStudentCurriculumDisplayName(curriculum)}
          </MenuItem>
        ));

        return [...acc, ...curriculumMenuItems];
      }, [] as JSX.Element[]),
    [
      products,
      productFamilyId,
      useProductGroups,
      disabledProductIds,
      assignedProductId,
      classes.curriculumMenuItem,
      classes.productMenuItem
    ]
  );

  const displayValue = useMemo(() => {
    let result = '';

    const curriculums = products.flatMap(product => product.curriculums ?? []);
    const selectedCurriculum = curriculums.find(curriculum => curriculum.id === value);
    if (selectedCurriculum) result = getStudentCurriculumDisplayName(selectedCurriculum);

    return result;
  }, [products, value]);

  return (
    <Select
      size="small"
      placeholder={t('Select Product')}
      onChange={onChange}
      value={value}
      displayValue={displayValue}
      className={cx(!value && classes.error)}
    >
      {menuItems}
    </Select>
  );
};

const useStyles = makeStyles(theme => ({
  error: {
    border: `2px solid ${theme.palette.error.light}`,
    borderRadius: 5
  },
  curriculumMenuItem: {
    paddingLeft: theme.spacing(2)
  },
  productMenuItem: {
    color: theme.typography.body1.color,
    fontWeight: theme.typography.fontWeightBold,
    backgroundColor: theme.palette.grey[100],
    opacity: '1 !important'
  }
}));

export default StudentCurriculumSelect;
